// Grid line movement functionality for PdfTableViewer
import {
  getCanvasCoordinates,
  cloneGridLines,
  detectGridLineChanges,
} from "./pdfTableViewerUtils";

/**
 * Create grid line movement functionality
 */
export const createGridLineMovementFunctions = (
  canvasRef,
  highlightContainerRef,
  dynamicGridLines,
  setDynamicGridLines,
  currentPage,
  pdfPageDimensions,
  drawingMode,
  isResizing,
  gridLineDragMode,
  isDraggingGridLine,
  setIsDraggingGridLine,
  draggingGridLine,
  setDraggingGridLine,
  gridLineStartPos,
  setGridLineStartPos,
  originalGridLines,
  setOriginalGridLines
) => {
  /**
   * Start dragging a grid line
   */
  const startGridLineDrag = (event, lineType, lineIndex) => {
    if (drawingMode || isResizing || !gridLineDragMode) return; // Don't interfere with other interactions

    event.preventDefault();
    event.stopPropagation();

    const coords = getCanvasCoordinates(
      event,
      canvasRef,
      highlightContainerRef
    );
    if (!coords) return;

    // Store original grid lines for potential rollback
    // BUT NOT if we're in a placement workflow where original state is already correctly set
    if (dynamicGridLines && !originalGridLines) {
      setOriginalGridLines(cloneGridLines(dynamicGridLines));
    }

    setIsDraggingGridLine(true);
    setDraggingGridLine({ type: lineType, index: lineIndex });
    setGridLineStartPos(coords);
  };

  /**
   * Continue dragging a grid line
   */
  const continueGridLineDrag = (event) => {
    if (!isDraggingGridLine || !draggingGridLine || !gridLineStartPos) {
      return;
    }

    const coords = getCanvasCoordinates(
      event,
      canvasRef,
      highlightContainerRef
    );
    if (!coords) return;

    // Calculate the delta movement
    const deltaX = coords.x - gridLineStartPos.x;
    const deltaY = coords.y - gridLineStartPos.y;

    // Update the grid line position in real-time
    if (dynamicGridLines?.[currentPage]) {
      const updatedGridLines = { ...dynamicGridLines };
      const pageLines = { ...updatedGridLines[currentPage] };

      if (
        draggingGridLine.type === "horizontal" &&
        pageLines.horizontal?.[draggingGridLine.index]
      ) {
        const line = { ...pageLines.horizontal[draggingGridLine.index] };
        const pdfDelta =
          (deltaY / (canvasRef.current?.getBoundingClientRect().height || 1)) *
          pdfPageDimensions.current.height;
        line.y0 += pdfDelta;
        line.y1 += pdfDelta;
        pageLines.horizontal[draggingGridLine.index] = line;
      } else if (
        draggingGridLine.type === "vertical" &&
        pageLines.vertical?.[draggingGridLine.index]
      ) {
        const line = { ...pageLines.vertical[draggingGridLine.index] };
        const pdfDelta =
          (deltaX / (canvasRef.current?.getBoundingClientRect().width || 1)) *
          pdfPageDimensions.current.width;
        line.x0 += pdfDelta;
        line.x1 += pdfDelta;
        pageLines.vertical[draggingGridLine.index] = line;
      }

      updatedGridLines[currentPage] = pageLines;
      setDynamicGridLines(updatedGridLines);
      setGridLineStartPos(coords); // Update for next movement
    }
  };

  /**
   * Cancel grid line drag operation
   */
  const cancelGridLineDrag = () => {
    // Restore original grid lines if available
    if (originalGridLines) {
      setDynamicGridLines(originalGridLines);
      setOriginalGridLines(null);
    }

    // Reset all drag states
    setIsDraggingGridLine(false);
    setDraggingGridLine(null);
    setGridLineStartPos(null);
  };

  /**
   * Finish grid line drag operation with confirmation
   */
  const finishGridLineDrag = (
    tableData,
    selectedTable,
    setPendingGridLineChanges,
    setShowGridLineConfirmation,
    recalculateCellsFromGridLines,
    placingGridLineType = null
  ) => {
    if (!isDraggingGridLine || !originalGridLines || !dynamicGridLines) {
      cancelGridLineDrag();
      return;
    }

    const originalGridLinesForPage = originalGridLines[currentPage];
    const newGridLinesForPage = dynamicGridLines[currentPage];

    if (!originalGridLinesForPage || !newGridLinesForPage) {
      cancelGridLineDrag();
      return;
    }

    // Detect if there are significant changes
    const changes = detectGridLineChanges(
      originalGridLinesForPage,
      newGridLinesForPage
    );

    if (!changes.hasChanges) {
      cancelGridLineDrag();
      return;
    }

    // Calculate new table data from moved grid lines
    const newTableData = recalculateCellsFromGridLines(
      dynamicGridLines,
      currentPage,
      selectedTable,
      tableData,
      originalGridLines,
      pdfPageDimensions
    );

    if (!newTableData) {
      cancelGridLineDrag();
      return;
    }

    // Store pending changes for confirmation
    const pendingChanges = {
      originalTableData: tableData,
      newTableData,
      originalGridLines,
      newGridLines: dynamicGridLines,
      movedLines: {
        vertical: Array.from(changes.movedVerticalLines),
        horizontal: Array.from(changes.movedHorizontalLines),
      },
      changeType: "gridLineMove",
    };

    setPendingGridLineChanges(pendingChanges);
    setShowGridLineConfirmation(true);

    // Keep drag state until user confirms/cancels
  };

  /**
   * Stop grid line drag (called on mouse up)
   */
  const stopGridLineDrag = (
    tableData,
    selectedTable,
    setPendingGridLineChanges,
    setShowGridLineConfirmation,
    recalculateCellsFromGridLines,
    placingGridLineType
  ) => {
    if (!isDraggingGridLine) {
      return;
    }

    // For placement mode (new rows), handle row confirmation
    if (placingGridLineType === "row") {
      // For rows, directly proceed to confirmation
      finishGridLineDrag(
        tableData,
        selectedTable,
        setPendingGridLineChanges,
        setShowGridLineConfirmation,
        recalculateCellsFromGridLines,
        placingGridLineType
      );
      return;
    }

    // For regular grid line movement (not placement)
    finishGridLineDrag(
      tableData,
      selectedTable,
      setPendingGridLineChanges,
      setShowGridLineConfirmation,
      recalculateCellsFromGridLines,
      placingGridLineType
    );
  };

  /**
   * Add mouse event listeners for grid line dragging
   */
  const addGridLineDragEventListeners = (
    tableData,
    selectedTable,
    setPendingGridLineChanges,
    setShowGridLineConfirmation,
    recalculateCellsFromGridLines,
    placingGridLineType
  ) => {
    const handleMouseMove = (e) => continueGridLineDrag(e);
    const handleMouseUp = () =>
      stopGridLineDrag(
        tableData,
        selectedTable,
        setPendingGridLineChanges,
        setShowGridLineConfirmation,
        recalculateCellsFromGridLines,
        placingGridLineType
      );

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // Return cleanup function
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  };

  return {
    startGridLineDrag,
    continueGridLineDrag,
    stopGridLineDrag,
    finishGridLineDrag,
    cancelGridLineDrag,
    addGridLineDragEventListeners,
  };
};
